import React, { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import * as pdfjsLib from 'pdfjs-dist';

// Use the bundled worker
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.mjs',
    import.meta.url
).toString();

interface PdfPreviewRendererProps {
    url: string | null;
}

/**
 * Renders a PDF blob URL as canvas images using pdf.js.
 * Works in all browsers without requiring a native PDF viewer plugin.
 */
export const PdfPreviewRenderer: React.FC<PdfPreviewRendererProps> = ({
    url,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [pageCount, setPageCount] = useState(0);

    useEffect(() => {
        if (!url) return;

        let cancelled = false;

        const render = async () => {
            try {
                setLoading(true);
                setError(null);

                const pdf = await pdfjsLib.getDocument(url).promise;

                if (cancelled) return;

                setPageCount(pdf.numPages);

                const container = containerRef.current;
                if (!container) return;

                // Clear previous canvases
                container.innerHTML = '';

                for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
                    if (cancelled) return;

                    const page = await pdf.getPage(pageNum);
                    // Scale to fit ~800px width (good for dialog)
                    const scale = 800 / page.getViewport({ scale: 1 }).width;
                    const viewport = page.getViewport({ scale });

                    const canvas = document.createElement('canvas');
                    canvas.width = viewport.width;
                    canvas.height = viewport.height;
                    canvas.style.width = '100%';
                    canvas.style.height = 'auto';
                    canvas.style.display = 'block';
                    canvas.style.marginBottom = '8px';
                    canvas.style.boxShadow = '0 1px 3px rgba(0,0,0,0.12)';

                    container.appendChild(canvas);

                    const ctx = canvas.getContext('2d');
                    if (ctx) {
                        await page.render({
                            canvasContext: ctx,
                            viewport,
                        }).promise;
                    }
                }

                setLoading(false);
            } catch (err) {
                if (!cancelled) {
                    console.error('PDF preview render error:', err);
                    setError('Failed to render PDF preview.');
                    setLoading(false);
                }
            }
        };

        render();

        return () => {
            cancelled = true;
        };
    }, [url]);

    if (error) {
        return (
            <Typography color="error" sx={{ p: 2 }}>
                {error}
            </Typography>
        );
    }

    return (
        <Box
            sx={{
                height: '100%',
                overflow: 'auto',
                bgcolor: '#e0e0e0',
                p: 1,
            }}
        >
            {loading && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                    }}
                >
                    <CircularProgress />
                    <Typography sx={{ ml: 2 }}>
                        Rendering preview
                        {pageCount > 0 ? ` (${pageCount} pages)...` : '...'}
                    </Typography>
                </Box>
            )}
            <Box
                ref={containerRef}
                sx={{
                    display: loading ? 'none' : 'block',
                    maxWidth: 800,
                    mx: 'auto',
                }}
            />
        </Box>
    );
};
